import {PriceProductProps, ProductProps, tableKeys} from '@/types';
import {useContext, createContext, useState, useRef, useCallback} from 'react';
import { useTables } from './TablesContext';
import { useClient } from './ClientContext';

export type Tables = (typeof tableKeys)[number];
interface LoadingContextType {
  catalog: Record<number, ProductProps[]>;
  isLoading: React.MutableRefObject<boolean>;
  calculatedList: React.MutableRefObject<Record<string, PriceProductProps[]>>;
  setProducts: (client: number, catalog: ProductProps[]) => void;
  isLoaded: React.MutableRefObject<Record<number, boolean>>;
}

const LoadingContext = createContext({} as LoadingContextType);
const MAX_CALCULATED_CACHE_KEYS = 1500;
const MAX_CALCULATED_CACHE_KEYS_PER_CLIENT = 600;

const shouldUseLowMemoryCatalogMode = () => {
  if (typeof navigator === 'undefined') return false;

  const nav = navigator as Navigator & { deviceMemory?: number };
  const ua = nav.userAgent || '';
  const isAndroid = /Android/i.test(ua);
  const isOldAndroid = /Android\s(4|5|6)\b/i.test(ua);

  if (typeof nav.deviceMemory === 'number') {
    return nav.deviceMemory <= 2;
  }

  return isAndroid && isOldAndroid;
};

const PricesProvider = ({children}: React.PropsWithChildren) => {
  const {tables} = useTables();
  const {selectedClient} = useClient();
  const [catalog, setHookProducts] = useState<Record<number, ProductProps[]>>(
    {} as Record<number, ProductProps[]>
  );
  
  const isLoading = useRef<boolean>(false);
  const isLoadingProducts = useRef<boolean>(false);
  const isLoaded = useRef<Record<number, boolean>>({});
  const calculatedList = useRef<Record<string, PriceProductProps[]>>({});
  const lowMemoryCatalogMode = useRef<boolean>(shouldUseLowMemoryCatalogMode());

  const pruneCalculatedCache = useCallback((client: number) => {
    const clientPrefix = `${client}-`;
    const keys = Object.keys(calculatedList.current);
    const clientKeys = keys.filter((key) => key.startsWith(clientPrefix));

    if (clientKeys.length > MAX_CALCULATED_CACHE_KEYS_PER_CLIENT) {
      const extraClientKeys = clientKeys.length - MAX_CALCULATED_CACHE_KEYS_PER_CLIENT;
      for (let i = 0; i < extraClientKeys; i++) {
        delete calculatedList.current[clientKeys[i]];
      }
    }

    const allKeys = Object.keys(calculatedList.current);
    if (allKeys.length > MAX_CALCULATED_CACHE_KEYS) {
      const extraGlobalKeys = allKeys.length - MAX_CALCULATED_CACHE_KEYS;
      for (let i = 0; i < extraGlobalKeys; i++) {
        delete calculatedList.current[allKeys[i]];
      }
    }
  }, []);
  
  const setProducts = useCallback((client: number, newCatalogProps: ProductProps[]) => {
    const currentClient = selectedClient;
    const newCatalog = !currentClient.UNILEVER ? newCatalogProps : newCatalogProps.filter(item => !item.UNILEVER)
    setHookProducts((prevCatalog) => {
      if (isLoadingProducts.current) return prevCatalog;
      isLoadingProducts.current = true;

      const restrictions = tables.current.restricaoProdutoCliente.reduce((acc: Record<number, boolean>, curr) => {
        if (curr.COD_CLIENTE === client) {
          acc[curr.COD_PRODUTO] = true;
        }
        return acc;
      }, {} as Record<number, boolean>);

      const catalogUltimaCompra = tables.current.ultima_compra;

      const hashUltimaCompra = catalogUltimaCompra?.reduce(
        (acc: { [x: string]: any; }, curr: { SEQPESSOA: number; SEQPRODUTO: string | number; }) => {
          if (curr.SEQPESSOA === client) {
            acc[curr.SEQPRODUTO] = curr;
          }
          return acc;
        },
        {} as Record<number, any>
      );

      // Mapeia os produtos e anexa o objeto ultimaCompra somente se encontrar
      const updatedProducts = newCatalog.map((p) => ({
        ...p,
        ultimaCompra: hashUltimaCompra ? hashUltimaCompra[p.COD_PRODUTO] || null : null,
      }));

      const currentCatalog = updatedProducts.filter((p) => !restrictions[p.COD_PRODUTO]);
      isLoadingProducts.current = false;
      pruneCalculatedCache(client);

      if (!lowMemoryCatalogMode.current) {
        return { ...prevCatalog, [client]: currentCatalog };
      }

      // Em aparelhos antigos, manter apenas o cliente ativo evita estouro de memória.
      Object.keys(prevCatalog).forEach((key) => {
        const codClient = Number(key);
        if (codClient !== client) {
          delete isLoaded.current[codClient];
        }
      });

      const nextCalculated = {} as Record<string, PriceProductProps[]>;
      Object.entries(calculatedList.current).forEach(([key, value]) => {
        if (key.startsWith(`${client}-`)) {
          nextCalculated[key] = value;
        }
      });
      calculatedList.current = nextCalculated;

      return { [client]: currentCatalog };
    });
  }, [tables, pruneCalculatedCache]);

  return (
    <LoadingContext.Provider
      value={{catalog, setProducts, calculatedList, isLoading, isLoaded}}
    >
      {children}
    </LoadingContext.Provider>
  );
};

const usePrices = () => {
  const context = useContext(LoadingContext);

  if (!context)
    throw new Error(
      'O context useTables só pode ser usado dentro de seu escopo. Erro ao usar hook useTables'
    );

  return context;
};

export {PricesProvider, usePrices};